import { PayloadAction } from "@reduxjs/toolkit";
import { put, takeLatest, call, all, select, CallEffect, putResolve } from "redux-saga/effects";

import { settings as appSettings } from "config";
import { applyHttpClientLocale } from "infrastructure/http";
import { commonMessages } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { INotificationActionResult, createNotificationResult } from "infrastructure/store";

import * as loc from "./localization-helpers";

import {
  locationChange,
  settingsActions,
  appTranslationActions,
  appNotificationActions,
  ITranslationMessagesLoadParam,
  IAppTranslationState,
  ITranslationAttributes,
  ISettingsState,
  ITranslationMessages,
  ITranslationMessagesGetResponse
} from "./";

function* loadMessageTranslationsExecute(action: PayloadAction<ITranslationMessagesLoadParam>) {
  const [appTranslation, settings]: [IAppTranslationState, ISettingsState] = yield select((state: { appTranslation: IAppTranslationState; settings: ISettingsState }) => {
    return [state.appTranslation, state.settings];
  });

  const opt = loc.getOptions(action.payload, settings, appTranslation);

  const data: ITranslationMessages = {
    messages: {},
    loadNewLocale: opt.loadNewLocale,
    locale: opt.locale
  };

  try {
    loc.loadCldrData();

    if (!loc.isSupportedLocale(opt.locale)) {
      yield put(
        appTranslationActions.loadMessageTranslationsFailed(
          createNotificationResult(data).withNotification(notification.error().useMessageId(commonMessages.error("localeNotSupported")))
        )
      );
      return;
    }

    const calls: CallEffect[] = [];

    for (const messagePrefix of appSettings.localization.messagePrefixes) {
      calls.push(call(() => loc.loadTranslationMessages(messagePrefix, appTranslation, opt)));
    }

    const responses: ITranslationMessagesGetResponse[] = yield all(calls);
    for (const response of responses) {
      const info: ITranslationAttributes = {
        lastModified: response.lastModified,
        messagePrefix: response.messagePrefix
      };

      data.messages = { ...data.messages, ...response.messages };
      yield put(appTranslationActions.loadMessageTranslationsPartCompleted(info));
    }

    if (opt.isFirstTimeInitialization) {
      yield putResolve(settingsActions.initializeLocale(data.locale));
    }

    yield put(appTranslationActions.loadMessageTranslationsCompleted(data));
  } catch (error) {
    yield put(
      appTranslationActions.loadMessageTranslationsFailed(
        createNotificationResult(data).withNotification(notification.error(error).useMessageId(commonMessages.error("localeNotSupported")))
      )
    );
  }
}

function* loadMessageTranslationsCompletedExecute(action: PayloadAction<ITranslationMessages>) {
  if (action.payload.loadNewLocale) {
    applyHttpClientLocale(action.payload.locale);
    yield put(settingsActions.changeLocaleCompleted(action.payload.locale));
  }
}

function* loadMessageTranslationsFailedExecute(action: PayloadAction<INotificationActionResult<ITranslationMessages>>) {
  if (action.payload.data && action.payload.data.loadNewLocale) {
    yield put(settingsActions.changeLocaleFailed(createNotificationResult(action.payload.data.locale).fromResult(action.payload)));
  }
}

function* changeLocaleExecute(action: PayloadAction<string>) {
  const loadParams: ITranslationMessagesLoadParam = {
    locale: action.payload,
    loadNewLocale: true
  };

  yield put(appTranslationActions.loadMessageTranslations(loadParams));
}

function* changeLocaleFailedExecute(action: PayloadAction<INotificationActionResult<string>>) {
  yield put(appNotificationActions.showAppNotification(action.payload.notification));
}

function* locationChangeExecute() {
  yield put(appNotificationActions.clearAppNotification());
  yield put(appNotificationActions.clearScreenNotification());
}

function* actionWatcher() {
  yield takeLatest(appTranslationActions.loadMessageTranslations.toString(), loadMessageTranslationsExecute);
  yield takeLatest(appTranslationActions.loadMessageTranslationsCompleted.toString(), loadMessageTranslationsCompletedExecute);
  yield takeLatest(appTranslationActions.loadMessageTranslationsFailed.toString(), loadMessageTranslationsFailedExecute);
  yield takeLatest(settingsActions.changeLocale.toString(), changeLocaleExecute);
  yield takeLatest(settingsActions.changeLocaleFailed.toString(), changeLocaleFailedExecute);
  yield takeLatest(locationChange.toString(), locationChangeExecute);
}

export function* appSaga() {
  yield all([actionWatcher()]);
}
